import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useT } from "@/lib/i18n/use-t";
import {
  type AssessmentModeDraft,
  newQuestion,
  type OptionDraft,
  type QuestionDraft,
  questionIsComplete,
} from "./assessment-drafts";
import { T } from "./assessment-mode-form.i18n";
import { QuestionEditDialog } from "./question-edit-dialog";

type AssessmentModeFormProps = {
  draft: AssessmentModeDraft;
  supportedLanguages: string[];
  disabled: boolean;
};

/**
 * One assessment mode's tab: an enable toggle plus, when enabled, its own
 * question pool, pass score and (quiz only) attempt rules. Each mode's pool is
 * fully independent of the other's.
 */
export function AssessmentModeForm({
  draft,
  supportedLanguages,
  disabled,
}: AssessmentModeFormProps) {
  const t = useT(T);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  const isQuiz = draft.mode === "QUIZ";
  const { questions, setQuestions } = draft;

  const updateQuestion = (id: string, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const updateOption = (questionId: string, optionId: string, patch: Partial<OptionDraft>) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.map((o) => (o.id === optionId ? { ...o, ...patch } : o)) }
          : q,
      ),
    );
  };

  const editingIndex = questions.findIndex((q) => q.id === editingQuestionId);
  const editingQuestion = editingIndex >= 0 ? questions[editingIndex]! : null;
  const idPrefix = isQuiz ? "quiz" : "questionnaire";

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-enabled`} className="cursor-pointer">
            {isQuiz ? t.enableQuiz : t.enableQuestionnaire}
          </Label>
          <p className="text-xs text-muted-foreground">
            {isQuiz ? t.quizHint : t.questionnaireHint}
          </p>
        </div>
        <Switch
          id={`${idPrefix}-enabled`}
          checked={draft.enabled}
          onCheckedChange={draft.setEnabled}
          disabled={disabled}
        />
      </div>

      {!draft.enabled ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {isQuiz ? t.disabledQuiz : t.disabledQuestionnaire}
        </p>
      ) : (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t.questions}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const q = newQuestion();
                  setQuestions((prev) => [...prev, q]);
                  setEditingQuestionId(q.id);
                }}
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t.addQuestion}
              </Button>
            </div>

            {/* Compact list — one row per question; editing opens a nested modal. */}
            {questions.map((question, qIndex) => {
              const complete = questionIsComplete(question);
              return (
                <div key={question.id} className="flex items-center gap-2 rounded-md border p-3">
                  <button
                    type="button"
                    onClick={() => setEditingQuestionId(question.id)}
                    className="min-w-0 flex-1 truncate text-left text-sm font-medium"
                  >
                    {question.label.trim() || `${t.question} ${qIndex + 1}`}
                  </button>
                  {isQuiz && question.category != null && (
                    <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                      {`${t.category} ${question.category}`}
                    </span>
                  )}
                  {!complete && (
                    <span className="shrink-0 text-xs text-destructive">{t.incomplete}</span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    tooltip={t.editAria}
                    className="shrink-0"
                    onClick={() => setEditingQuestionId(question.id)}
                    disabled={disabled}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    tooltip={t.removeQuestion}
                    className="shrink-0"
                    onClick={() => setQuestions((prev) => prev.filter((q) => q.id !== question.id))}
                    disabled={disabled || questions.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}

            {questions.length === 0 && (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                {t.noQuestions}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-min-score`}>{t.minScore}</Label>
            <Input
              id={`${idPrefix}-min-score`}
              type="number"
              step="any"
              className="w-32"
              value={draft.minScore}
              onChange={(e) => draft.setMinScore(e.target.value)}
              disabled={disabled}
            />
            <p className="text-xs text-muted-foreground">{t.minScoreHint}</p>
          </div>

          {isQuiz && (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quiz-questions-to-ask">
                  {draft.hasCategories ? t.questionsToAskPerCategory : t.questionsToAsk}
                </Label>
                <Input
                  id="quiz-questions-to-ask"
                  type="number"
                  min={1}
                  step={1}
                  value={draft.questionsToAsk}
                  onChange={(e) => draft.setQuestionsToAsk(e.target.value)}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">
                  {draft.hasCategories ? t.questionsToAskPerCategoryHint : t.questionsToAskHint}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiz-max-attempts">{t.maxAttempts}</Label>
                <Input
                  id="quiz-max-attempts"
                  type="number"
                  min={1}
                  step={1}
                  value={draft.maxAttempts}
                  onChange={(e) => draft.setMaxAttempts(e.target.value)}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">{t.maxAttemptsHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiz-attempt-reset-days">{t.attemptResetDays}</Label>
                <Input
                  id="quiz-attempt-reset-days"
                  type="number"
                  min={1}
                  step={1}
                  value={draft.attemptResetDays}
                  onChange={(e) => draft.setAttemptResetDays(e.target.value)}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">{t.attemptResetDaysHint}</p>
              </div>
            </div>
          )}
        </>
      )}

      <QuestionEditDialog
        question={editingQuestion}
        index={editingIndex}
        supportedLanguages={supportedLanguages}
        disabled={disabled}
        showCategory={isQuiz}
        onChange={(patch) => editingQuestion && updateQuestion(editingQuestion.id, patch)}
        onChangeOption={(optionId, patch) =>
          editingQuestion && updateOption(editingQuestion.id, optionId, patch)
        }
        onClose={() => setEditingQuestionId(null)}
      />
    </div>
  );
}
